import type { DataPlatformAPIResponse } from "../schemas/APISchemas.ts";
import type {
  TransactionDetailItemStatusType,
  TransactionDetailsType,
  TransactionDetailsV2Type,
  TransactionGetListFilterType,
  TransactionSummaryType,
  TransactionType,
} from "../schemas/TransactionSchemas.ts";
import type {
  GetListRequestType,
  GetOneRequestType,
  GetOneResponseType,
  ResourceAdapterType,
} from "../util/index.ts";
import { getPaginatedData, getSortedData } from "../util/index.ts";
import { restAPI } from "./api.ts";

const timestampHasTimezone = (timestamp: string) => {
  // Assume the only stated timezone will be UTC
  const timezoneDelimiters = ["z", "Z"];
  return timezoneDelimiters.some((timezoneChar) =>
    timestamp.includes(timezoneChar)
  );
};

// https://validere.atlassian.net/browse/CHB-3077
// Conditionally assert timezone is UTC
// TODO: Delete this later
const enrichTransactionTimestamp = (
  transaction: TransactionType
): TransactionType => ({
  ...transaction,
  created_at: timestampHasTimezone(transaction.created_at)
    ? transaction.created_at
    : `${transaction.created_at}Z`,
});

// TODO: Refactor sink/download types to resource types
type TransactionServiceType = Pick<
  ResourceAdapterType<TransactionType>,
  "getList"
> &
  Pick<ResourceAdapterType<TransactionType[]>, "getOne"> & {
    sink: {
      getOne: (inputs: {
        transactionId: string;
        hasTransformed?: boolean;
      }) => Promise<any>;
    };
    download: {
      getOne: ({
        transactionId,
        hasTransformed,
        onlyErrors,
      }: {
        transactionId: string;
        hasTransformed?: boolean;
        onlyErrors?: boolean;
      }) => Promise<any>;
    };
    retry: {
      createOne: (params: {
        transactionId: string;
        transactionRetryStage?: "transformation" | "ingestion";
        retryAll?: boolean;
      }) => Promise<DataPlatformAPIResponse<TransactionType, "Transaction">>;
    };
    details: {
      getOne: (
        params: GetOneRequestType<{
          accept?: string;
          status?: TransactionDetailItemStatusType;
          includeData?: boolean;
        }>
      ) => Promise<GetOneResponseType<TransactionDetailsType[]>>;
    };
    detailsV2: {
      getOne: (
        params: GetOneRequestType<{
          accept?: string;
          continuationToken?: string;
          pageSize?: number;
          statusFilter?: TransactionDetailItemStatusType;
          includeData?: boolean;
        }>
      ) => Promise<GetOneResponseType<TransactionDetailsV2Type>>;
    };
  };

export const TransactionAdapter: TransactionServiceType = {
  /**
   * Get a list of transactions
   * @see {@link https://sources-staging.dataplatform.validere.xyz/docs#/transactions/list_all_transactions_transactions_get}
   */
  getList: async ({
    filters,
    page,
    pageSize,
    sortBy,
    sortDirection,
  }: GetListRequestType<TransactionGetListFilterType>) => {
    const response = await restAPI.dataPlatformSourceAPI.GET<
      DataPlatformAPIResponse<TransactionType, "Transaction">
    >({
      endpoint: "/transactions",
      query: {
        ...filters,
      },
    });

    return getPaginatedData<TransactionType>(
      getSortedData(
        response.data.map(enrichTransactionTimestamp),
        sortBy as keyof TransactionType,
        sortDirection
      ),
      page,
      pageSize
    );
  },
  /**
   * Get a single transaction but its UUID
   * @see {@link https://sources-staging.dataplatform.validere.xyz/docs#/transactions/get_transaction_by_id_transactions__transaction_id__get}
   */
  getOne: ({ id, meta: { history } = {} }) =>
    restAPI.dataPlatformSourceAPI
      .GET<DataPlatformAPIResponse<TransactionType, "Transaction">>({
        endpoint: `/transactions/${id}`,
        query: {
          ...(history ? { history } : {}),
        },
      })
      .then((resp) => ({ data: resp.data.map(enrichTransactionTimestamp) })),
  download: {
    getOne: ({
      transactionId,
      hasTransformed,
      onlyErrors,
    }: {
      transactionId: string;
      hasTransformed?: boolean;
      onlyErrors?: boolean;
    }) =>
      restAPI.dataPlatformSourceAPI.GET({
        endpoint: `/transactions/${transactionId}/download`,
        query: {
          version: hasTransformed ? "transformed" : "submitted",
          error_only: onlyErrors ? true : false,
        },
        headers: {
          Accept: "*/*",
        },
        responseType: "blob",
      }),
  },
  retry: {
    /**
     * Retry a single transaction
     * @see {@link https://sources-staging.dataplatform.validere.xyz/docs#/transactions/retry_transaction_by_id_transactions__transaction_id__retry_post}
     */
    createOne: ({ transactionId, transactionRetryStage, retryAll }) =>
      restAPI.dataPlatformSourceAPI.POST({
        endpoint: `/transactions/${transactionId}/retry`,
        query: {
          ...(transactionRetryStage
            ? { transaction_retry_stage: transactionRetryStage }
            : {}),
          ...(retryAll !== undefined ? { retry_all: retryAll } : {}),
        },
      }),
  },
  sink: {
    /**
     * Get the sink summary for a transaction
     * @see {@link https://sources-staging.dataplatform.validere.xyz/docs#/transactions/get_summary_by_transaction_by_id_transactions__transaction_id__sink_summary_get}
     */
    getOne: ({ transactionId }: { transactionId: string }) =>
      restAPI.dataPlatformSourceAPI
        .GET<TransactionSummaryType>({
          endpoint: `/transactions/${transactionId}/sink/summary`,
        })
        .then((data) => ({
          transaction_id: transactionId,
          sink: data,
        })),
  },
  details: {
    getOne: async ({ id, meta: { accept, status, includeData } = {} }) => {
      /** Return a blob if a specific return type is requested */
      if (accept) {
        return restAPI.dataPlatformSourceAPI.GET<
          DataPlatformAPIResponse<TransactionDetailsType, "TransactionDetails">
        >({
          endpoint: `/transactions/${id}/details`,
          headers: {
            accept,
          },
          responseType: "blob",
          query: {
            ...(status ? { status_filter: status } : {}),
            ...(includeData ? { include_data: includeData } : {}),
          },
        });
      }

      const { data } = await restAPI.dataPlatformSourceAPI.GET<
        DataPlatformAPIResponse<TransactionDetailsType, "TransactionDetails">
      >({
        endpoint: `/transactions/${id}/details`,
        headers: {
          accept: "application/json",
        },
      });
      return { data };
    },
  },
  detailsV2: {
    /**
     * Get a single page of transaction detail rows using cursor-based pagination.
     * Pass the `continuationToken` from the previous response's `data.continuation_token`
     * to advance to the next page; there is no way to jump to an arbitrary page.
     *
     * Passing `accept` (e.g. `"text/csv"` or the XLSX media type) switches to
     * bulk export mode instead: `continuationToken`/`pageSize` are ignored, and
     * every row matching `statusFilter` (or every row, if omitted) is resolved
     * server-side in one request and returned as a downloadable file —
     * `statusFilter: "failed"` resolves via the `failure_index` GSI.
     * @see {@link https://sources-staging.dataplatform.validere.xyz/docs#/transactions/get_transaction_details_page_v2_v2_transactions__transaction_id__details_get}
     */
    getOne: async ({
      id,
      meta: {
        accept,
        continuationToken,
        pageSize,
        statusFilter,
        includeData,
      } = {},
    }) => {
      /** Return a blob if a specific export format is requested */
      if (accept) {
        return restAPI.dataPlatformSourceAPI.GET<
          GetOneResponseType<TransactionDetailsV2Type>
        >({
          endpoint: `/v2/transactions/${id}/details`,
          headers: {
            accept,
          },
          responseType: "blob",
          query: {
            ...(statusFilter ? { status_filter: statusFilter } : {}),
            ...(includeData !== undefined ? { include_data: includeData } : {}),
          },
        });
      }

      const { data } = await restAPI.dataPlatformSourceAPI.GET<
        DataPlatformAPIResponse<TransactionDetailsV2Type, "TransactionDetails">
      >({
        endpoint: `/v2/transactions/${id}/details`,
        headers: {
          accept: "application/json",
        },
        query: {
          ...(pageSize ? { page_size: pageSize } : {}),
          ...(continuationToken
            ? { continuation_token: continuationToken }
            : {}),
          ...(statusFilter ? { status_filter: statusFilter } : {}),
          ...(includeData !== undefined ? { include_data: includeData } : {}),
        },
      });
      return { data: data[0] };
    },
  },
};
